import { Injectable } from "@angular/core";
import { ApiService } from "./api.service";
import {
    IPaymentServiceArgs,
    IPaymentStatusService,
    PaymentServiceStatus,
    PaymentStatus,
    PaymentResult,
    OpenBankingPayment,
    MbWayPayment,
    AgreementContact,
    AgreementAgreementSend,
    AgreementValidated,
    AgreementEligibility,
    AgreementGenerate,
    AgreementCheckout,
    AgreementHistory,
    AgreementHistoryUserStatus,
} from "@edp-models/edp-payment.model";
import { EdpResponseBody } from "@edp-models/response.model";
import { map, catchError, filter, take, concatMap, timeout, switchMap, tap, shareReplay } from "rxjs/operators";
import { of, Observable, throwError } from "rxjs";
import { Store, select } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { getRecaptchaResponse } from "@edp-store/selectors/dashboard.selectors";
import { Contract } from "@edp-components/edp-contract";
import moment from "moment";
import { SetRecaptcha } from "@edp-store/actions/dashboard.actions";
import { getUser } from "@edp-store/selectors/user.selectors";
import { EdpUser } from "@edp-models/user.model";

@Injectable({
    providedIn: "root",
})
export class PaymentService {
    private static openPayments: { [id: string]: Observable<PaymentResult> }[] = [];
    public user: EdpUser;

    constructor(private apiService: ApiService, private store: Store<IAppState>) {
        this.store.select(getUser).subscribe(user => {
            this.user = user;
        });
    }

    public static getPaymentObservable(payment: PaymentResult | string) {
        if ((payment as PaymentResult).id) {
            return PaymentService.openPayments[(payment as PaymentResult).id] || null;
        } else {
            return PaymentService.openPayments[payment as string] || null;
        }
    }

    public triggerMbwayPayment(phone: string, contract: Contract, amount: number, debt: boolean): Observable<MbWayPayment> {
        const payment = new MbWayPayment(phone, contract, amount, debt);
        return this.triggerPayment(payment, this.createMbwayPayment.bind(this), (args: IPaymentServiceArgs) =>
            this.apiService.callServicePostToCollection<IPaymentStatusService[]>("payment.getMBWayPayment", args)
        );
    }

    public triggerOpenbankingPayment(
        bankIdentifier: string,
        bankName: string,
        contract: Contract,
        amount: number,
        debt: boolean
    ): Observable<OpenBankingPayment> {
        const payment = new OpenBankingPayment(bankName, bankIdentifier, contract, amount, debt);
        return this.triggerPayment(payment, this.createOpenBankingPayment.bind(this), (args: IPaymentServiceArgs) =>
            this.apiService.callServicePostToCollection<IPaymentStatusService[]>("payment.getOpenBankPayment", args)
        );
    }

    public createMbwayPayment(payment: MbWayPayment): Observable<MbWayPayment> {
        payment.status = PaymentStatus.PAYMENT_CREATED;
        return this.apiService.callServicePost("payment.createPaymentMBWay", payment.getRequestArgs()).pipe(
            catchError((result: Error) => {
                // upstream error found
                payment.status = PaymentStatus.PAYMENT_FAILED;
                payment.error = result;
                return throwError(payment.setStatus(PaymentStatus.PAYMENT_FAILED, result));
            }),
            switchMap((response: EdpResponseBody) => {
                if (!response.Success || !response.Result || !response.Result.Collection || !response.Result.Collection.Success) {
                    // error found
                    payment.error = new Error(
                        response.Result.ErrorMessage || "Unkown error found when trying to create payment in provider"
                    );
                    return throwError(payment.setStatus(PaymentStatus.PAYMENT_FAILED));
                }

                payment.requestDate = moment();
                return of(payment.setStatus(PaymentStatus.PAYMENT_CREATED));
            })
        );
    }

    public createOpenBankingPayment(payment: OpenBankingPayment): Observable<OpenBankingPayment> {
        payment.status = PaymentStatus.PAYMENT_CREATED;
        // change the service name to the openBanking one!
        return this.apiService.callServicePost("payment.createPaymentOpenBank", payment.getRequestArgs()).pipe(
            catchError((result: Error) => {
                // upstream error found
                payment.status = PaymentStatus.PAYMENT_FAILED;
                payment.error = result;
                return throwError(payment.setStatus(PaymentStatus.PAYMENT_FAILED, result));
            }),
            switchMap((response: EdpResponseBody) => {
                if (!response.Success || !response.Result || !response.Result.Collection || !response.Result.Collection.Success) {
                    // error found
                    payment.status = PaymentStatus.PAYMENT_FAILED;
                    payment.error = new Error(
                        response.Result.ErrorMessage || "Unkown error found when trying to create payment in provider"
                    );
                    return throwError(payment.setStatus(PaymentStatus.PAYMENT_FAILED, payment.error));
                }

                payment.requestDate = moment();
                payment.redirectUrl = response.Result.Collection.redirectUrl;
                return of(payment.setStatus(PaymentStatus.PAYMENT_CREATED));
            })
        );
    }

    protected triggerPayment(
        payment: PaymentResult,
        createPaymentFn: (p: PaymentResult) => Observable<PaymentResult>,
        paymentStatusFn: (p: IPaymentServiceArgs) => Observable<IPaymentStatusService[] | Error>
    ): Observable<PaymentResult> {
        if (PaymentService.openPayments[payment.id]) {
            return PaymentService.openPayments[payment.id];
        }

        payment.status = PaymentStatus.RECAPTCHA_GET;

        PaymentService.openPayments[payment.id] = new Observable<PaymentResult>(subscriber => {
            payment.status = PaymentStatus.RECAPTCHA_GET;

            const sendValue = (payment: PaymentResult) => {
                subscriber.next(payment.getCopy());
            };

            of(payment)
                .pipe(
                    tap(sendValue),
                    concatMap(this.getRecaptcha.bind(this)),
                    tap(sendValue),

                    // call service to create the payment
                    concatMap(createPaymentFn),
                    tap(sendValue),

                    // Start monitorizing
                    concatMap(payment => {
                        return this.getPaymentStatusObservable(paymentStatusFn, payment).pipe(
                            tap({ error: sendValue }),
                            ApiService.addDelayedRetry(5000, moment().add(payment instanceof MbWayPayment ? 6 : 26, "m")),
                            catchError((payment: PaymentResult) => {
                                // if we endup in here with an exception, it means
                                // we have a timeout in the payment
                                return throwError(
                                    payment.setStatus(PaymentStatus.PAYMENT_STATUS_EXPIRED, new Error("Max payment time exceeded"))
                                );
                            })
                        );
                    }),

                    catchError((payment: PaymentResult) => {
                        return of(payment);
                    }),

                    tap(p => {
                        if (p.status !== PaymentStatus.PAYMENT_STATUS_SUCCESS) {
                            // allow retries
                            delete PaymentService.openPayments[payment.id];
                        }
                        sendValue(p);
                    })
                )
                .subscribe();
        });
        return (PaymentService.openPayments[payment.id] = PaymentService.openPayments[payment.id].pipe(shareReplay()));
    }

    public getPaymentStatusObservable(
        service: (p: IPaymentServiceArgs) => Observable<IPaymentStatusService[] | Error>,
        payment: PaymentResult
    ): Observable<PaymentResult> {
        payment.setStatus(PaymentStatus.PAYMENT_STATUS_PENDING);
        return service(payment.getRequestArgs()).pipe(
            concatMap((response: IPaymentStatusService[]) => {
                if (!response.length) {
                    return throwError(payment);
                }

                switch (response[0].status.toUpperCase()) {
                    case PaymentServiceStatus.SUCCESS:
                        payment.setStatus(PaymentStatus.PAYMENT_STATUS_SUCCESS);
                        break;
                    case PaymentServiceStatus.REJECTED:
                        payment.setStatus(PaymentStatus.PAYMENT_STATUS_REJECTED);
                        break;
                    case PaymentServiceStatus.CANCELLED:
                        payment.setStatus(PaymentStatus.PAYMENT_STATUS_CANCELLED);
                        break;
                    case PaymentServiceStatus.EXPIRED:
                        payment.setStatus(PaymentStatus.PAYMENT_STATUS_EXPIRED);
                        break;
                    case PaymentServiceStatus.PENDING:
                        payment.setStatus(PaymentStatus.PAYMENT_STATUS_PENDING);
                        break;
                    // default is pending
                }

                return of(payment);
            }),
            catchError((err: Error) => {
                return of(payment);
            }),
            concatMap((p: PaymentResult) => {
                // this will trigger the retry
                return p.status === PaymentStatus.PAYMENT_STATUS_PENDING ? throwError(p) : of(p);
            })
        );
    }

    protected getRecaptcha(payment: PaymentResult): Observable<PaymentResult> {
        const ob = this.store
            .pipe(
                select(getRecaptchaResponse),
                filter(v => !!v),
                take(1),
                timeout(60000)
            )
            .pipe(
                map((recaptchaResult: string) => {
                    if (recaptchaResult) {
                        payment.captchaToken = recaptchaResult;
                        payment.setStatus(PaymentStatus.RECAPTCHA_RECEIVED);
                    } else {
                        payment.setStatus(PaymentStatus.RECAPTCHA_FAILED, new Error("Failed to retrieve recaptcha"));
                    }

                    return payment;
                }),
                catchError(r => {
                    // timeout

                    return throwError(
                        payment.setStatus(PaymentStatus.RECAPTCHA_FAILED, new Error("Failed to retrieve recaptcha, request timeout"))
                    );
                })
            );
        this.store.dispatch(new SetRecaptcha(true));

        return ob;
    }

    public agreementContact(args: AgreementContact) {
        const userStatus = this.user ? AgreementHistoryUserStatus.PRIVATE : AgreementHistoryUserStatus.PUBLIC;
        return this.apiService.callServicePostToCollectionCatalog("payment.agreement.contact", { user: userStatus, ...args });
    }

    public generateToken(args: AgreementAgreementSend) {
        const userStatus = this.user ? AgreementHistoryUserStatus.PRIVATE : AgreementHistoryUserStatus.PUBLIC;
        return this.apiService.callServicePostToCollectionCatalog("payment.agreement.send", { user: userStatus, ...args });
    }

    public validateToken(args: AgreementValidated) {
        const userStatus = this.user ? AgreementHistoryUserStatus.PRIVATE : AgreementHistoryUserStatus.PUBLIC;
        return this.apiService.callServicePostToCollectionCatalog("payment.agreement.validated", { user: userStatus, ...args });
    }

    public eligibilityAgreement(args: AgreementEligibility) {
        const userStatus = this.user ? AgreementHistoryUserStatus.PRIVATE : AgreementHistoryUserStatus.PUBLIC;
        return this.apiService.callServicePostToCollectionCatalog("payment.agreement.eligibility", { user: userStatus, ...args });
    }

    public agreementGenerate(args: AgreementGenerate) {
        const userStatus = this.user ? AgreementHistoryUserStatus.PRIVATE : AgreementHistoryUserStatus.PUBLIC;
        return this.apiService.callServicePostToCollectionCatalog("payment.agreement.generate", { user: userStatus, ...args });
    }

    public agreementCheckout(args: AgreementCheckout) {
        const userStatus = this.user ? AgreementHistoryUserStatus.PRIVATE : AgreementHistoryUserStatus.PUBLIC;
        return this.apiService.callServicePostToCollectionCatalog("payment.agreement.status", { user: userStatus, ...args });
    }

    public agreementHistory(args: AgreementHistory) {
        const userStatus = this.user ? AgreementHistoryUserStatus.PRIVATE : AgreementHistoryUserStatus.PUBLIC;
        return this.apiService.callServicePostToCollectionCatalog("payment.agreement.history", { user: userStatus, ...args });
    }

    public agreementConfirmation(args: any) {
        const userStatus = this.user ? AgreementHistoryUserStatus.PRIVATE : AgreementHistoryUserStatus.PUBLIC;
        return this.apiService.callServicePostToCollectionCatalog("payment.agreement.confirmation", { user: userStatus, ...args });
    }

    public agreementReject(args: any) {
        const userStatus = this.user ? AgreementHistoryUserStatus.PRIVATE : AgreementHistoryUserStatus.PUBLIC;
        return this.apiService.callServicePostToCollectionCatalog("payment.agreement.reject", { user: userStatus, ...args });
    }
}
